import "./Fretboard.scss";

import React, { PureComponent, createRef } from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import times from "lodash/times";

import AudioKey from "../../lib/AudioKey";
import FretboardFret from "./FretboardFret";
import FretboardFretHead from "./FretboardFretHead";
import FretboardString from "./FretboardString";
import FretboardStringHead from "./FretboardStringHead";
import { fretNumber, orientationIsFlipped, stringNumber } from "./lib";
import { getDefaultOrientation } from "../../lib/layout";
import metaActions from "../../state/meta/actions";
import store from "../../store";
import { defaultFretAmount } from "../../constants";

const getLocations = function(strings, frets) {
	return strings.map((loc) => {
		return times(1 + frets, () => {
			let out = loc;
			loc = loc.next();
			return out;
		});
	});
};

class Fretboard extends PureComponent {
	static propTypes = {
		dots: PropTypes.array,
		frets: PropTypes.number,
		highlight: PropTypes.number,
		instrument: PropTypes.string,
		orientation: PropTypes.string,
		relativeKey: PropTypes.instanceOf(AudioKey),
		selectedKey: PropTypes.instanceOf(AudioKey),
		stringLayout: PropTypes.string,
		strings: PropTypes.array.isRequired,
		title: PropTypes.string
	};

	state = {
		scrolledAwayFromLeft: false,
		scrolledAwayFromRight: false
	};

	containerInnerRef = createRef();

	onMouseOut = debounce(() => {
		store.dispatch(metaActions.update({ highlight: null }));
	}, 200, { leading: true, trailing: false });

	onScroll = () => {
		this.setScrollState();
	};

	componentDidMount() {
		this.onScroll();
		window.addEventListener("resize", this.onScroll);
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.onScroll);
	}

	setScrollState() {
		const containerInner = this.containerInnerRef.current;
		const table = containerInner?.children[0];

		if (!table) {
			this.setState({
				scrolledAwayFromLeft: false,
				scrolledAwayFromRight: false
			});

			return;
		}

		this.setState({
			scrolledAwayFromLeft: containerInner.scrollLeft > 0,
			scrolledAwayFromRight:
				containerInner.scrollLeft + containerInner.offsetWidth <
				table.offsetWidth
		});
	}

	renderFlippedBoard() {
		let {
			dots,
			frets = defaultFretAmount,
			highlight,
			instrument,
			orientation = getDefaultOrientation(),
			relativeKey,
			selectedKey,
			stringLayout,
			strings
		} = this.props;

		let locations = getLocations(strings, frets);

		let stringHeads = times(strings.length, (index) => {
			let n = stringNumber(index, strings, orientation, stringLayout);
			return <FretboardStringHead string={n} scope="col" key={n} />;
		});

		let className = `fretboard fretboard--flipped fretboard--${orientation}` +
			(stringLayout ? ` fretboard--${stringLayout}` : "");

		return (
			<table className={className}>
				{ orientation !== "btt" && (
					<thead>
						<tr>
							<th scope="col" className="corner" />
							{ stringHeads }
						</tr>
					</thead>
				) }
				<tbody onMouseOut={this.onMouseOut}>
					{ times(1 + frets, (i) => (
						<FretboardFret
							dots={dots}
							frets={frets}
							highlight={highlight}
							index={i}
							instrument={instrument}
							locations={locations}
							orientation={orientation}
							relativeKey={relativeKey}
							selectedKey={selectedKey}
							stringLayout={stringLayout}
							strings={strings}
							key={i} />
					)) }
				</tbody>
				{ orientation === "btt" && (
					<tfoot>
						<tr>
							<th scope="col" className="corner" />
							{ stringHeads }
						</tr>
					</tfoot>
				) }
			</table>
		);
	}

	renderBoard() {
		let {
			dots,
			frets = defaultFretAmount,
			highlight,
			instrument,
			orientation = getDefaultOrientation(),
			relativeKey,
			selectedKey,
			stringLayout,
			strings
		} = this.props;

		if (orientationIsFlipped(orientation)) {
			return this.renderFlippedBoard();
		}

		let locations = getLocations(strings, frets);

		let className = `fretboard fretboard--${orientation}` +
			(stringLayout ? ` fretboard--${stringLayout}` : "");

		return (
			<table className={className}>
				<thead>
					<tr>
						{ orientation !== "rtl" && (
							<th scope="col" className="corner" />
						) }
						{ times(1 + frets, (n) => (
							<FretboardFretHead
								fret={fretNumber(n, frets, orientation)}
								orientation={orientation}
								key={`0-${n}`} />
						)) }
						{ orientation === "rtl" && (
							<th scope="col" className="corner" />
						) }
					</tr>
				</thead>
				<tbody onMouseOut={this.onMouseOut}>
					{ times(strings.length, (i) => (
						<FretboardString
							dots={dots}
							frets={frets}
							highlight={highlight}
							index={i}
							instrument={instrument}
							locations={locations}
							orientation={orientation}
							relativeKey={relativeKey}
							selectedKey={selectedKey}
							stringLayout={stringLayout}
							strings={strings}
							key={i} />
					)) }
				</tbody>
			</table>
		);
	}

	render() {
		const { title } = this.props;
		const { scrolledAwayFromLeft, scrolledAwayFromRight } = this.state;

		const cnb = "fretboard-container";
		const containerClassName = cnb +
			(scrolledAwayFromLeft ? ` ${cnb}--away-from-left` : "") +
			(scrolledAwayFromRight ? ` ${cnb}--away-from-right` : "");

		const content = (
			<div className={containerClassName}>
				<div
					className="fretboard-container__inner"
					onScroll={this.onScroll}
					ref={this.containerInnerRef}
				>
					{this.renderBoard()}
				</div>
			</div>
		);

		if (title) {
			return (
				<div>
					<h2 className="fretboard-title">{title}</h2>
					{content}
				</div>
			);
		}

		return content;
	}
}

export default Fretboard;
